import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage: React.FC = () => {
    const lastUpdated = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Home
                    </Link>
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
                    <p className="text-gray-600">Last updated: {lastUpdated}</p>
                </div>

                {/* Content */}
                <div className="bg-white rounded-2xl shadow-soft p-8 sm:p-12 space-y-8 text-gray-700 leading-relaxed">

                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                        <p className="mb-4">
                            Welcome to GymPe ("we," "our," or "us") product by wescalex technologies llp. We are committed to protecting your privacy and ensuring the security of your personal information.
                            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our gym management software and related services (collectively, the "Service").
                        </p>
                        <p>
                            By accessing or using our Service, you signify that you have read, understood, and agree to our collection, storage, use, and disclosure of your personal information as described in this Privacy Policy and our Terms of Service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900">2.1 Personal Information</h3>
                            <p>
                                We may collect personal information that you voluntarily provide to us when you register for the Service, express an interest in obtaining information about us or our products and services, when you participate in activities on the Service, or otherwise when you contact us. This may include:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Name and contact data (email address, phone number, physical address)</li>
                                <li>Credentials (passwords, security questions)</li>
                                <li>Payment data (credit card information, billing address) - processed securely by our payment processors</li>
                                <li>Business information (gym name, business address, tax ID)</li>
                            </ul>

                            <h3 className="text-lg font-semibold text-gray-900 mt-6">2.2 Usage Data</h3>
                            <p>
                                We automatically collect certain information when you visit, use, or navigate the Service. This information does not reveal your specific identity (like your name or contact information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, information about how and when you use our Service, and other technical information.
                            </p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                        <p className="mb-4">We use personal information collected via our Service for a variety of business purposes described below:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>To provide and maintain our Service, including managing your account and gym operations.</li>
                            <li>To process your payments and manage your subscription.</li>
                            <li>To communicate with you, including sending administrative information, updates, and security alerts.</li>
                            <li>To respond to your inquiries and offer support.</li>
                            <li>To improve our Service, marketing, and user experience.</li>
                            <li>To enforce our terms, conditions, and policies for business purposes, to comply with legal and regulatory requirements or in connection with our contract.</li>
                            <li>To prevent fraud and ensure the security of our platform.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
                        <p className="mb-4">We may share your information in the following situations:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>
                                <strong>Service Providers:</strong> We may share your data with third-party vendors, service providers, contractors, or agents who perform services for us or on our behalf and require access to such information to do that work (e.g., payment processing, data analysis, email delivery, hosting services).
                            </li>
                            <li>
                                <strong>Integration Partners:</strong> We may share data with integration partners, such as WhatsApp (Meta), to facilitate specific features like automated notifications and messaging campaigns, subject to your configuration and authorization.
                            </li>
                            <li>
                                <strong>Business Transfers:</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
                            </li>
                            <li>
                                <strong>Legal Requirements:</strong> We may disclose your information where we are legally required to do so in order to comply with applicable law, governmental requests, a judicial proceeding, court order, or legal process.
                            </li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                        <p>
                            We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights</h2>
                        <p className="mb-4">Depending on your location, you may have certain rights regarding your personal information, including:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>The right to request access and obtain a copy of your personal information.</li>
                            <li>The right to request rectification or erasure;</li>
                            <li>The right to restrict the processing of your personal information;</li>
                            <li>The right to data portability; and</li>
                            <li>The right to object to the processing of your personal information.</li>
                        </ul>
                        <p className="mt-4">
                            To exercise any of these rights, please contact us using the contact information provided below.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cookies and Tracking Technologies</h2>
                        <p>
                            We may use cookies and similar tracking technologies (like web beacons and pixels) to access or store information. Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Policy.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Third-Party Websites</h2>
                        <p>
                            The Service may contain advertisements from third parties that are not affiliated with us and which may link to other websites, online services, or mobile applications. We cannot guarantee the safety and privacy of data you provide to any third parties. Any data collected by third parties is not covered by this privacy policy. We are not responsible for the content or privacy and security practices and policies of any third parties, including other websites, services, or applications that may be linked to or from the Service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Updates to This Policy</h2>
                        <p>
                            We may update this privacy policy from time to time. The updated version will be indicated by an updated "Revised" date and the updated version will be effective as soon as it is accessible. If we make material changes to this privacy policy, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this privacy policy frequently to be informed of how we are protecting your information.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
                        <p className="mb-4">
                            If you have questions or comments about this policy, you may email us at hello@gympe.in or by post to:
                        </p>
                        <address className="not-italic bg-gray-50 p-6 rounded-xl border border-gray-100">
                            <strong>Wescalex Technologies llp</strong><br />
                            Attn: Designated partners<br />
                            Hyderabad, Telangana, India<br />
                        </address>
                    </section>
                </div>

                <div className="mt-8 text-center text-gray-500 text-sm">
                    &copy; {new Date().getFullYear()} GymPe. All rights reserved.
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;
